import React, { useEffect } from "react";
import { Organization } from "../../../../Entities/Organization";
import { Button, Divider, Form, Input, Modal, Select } from "antd";
import { useLoader } from "../../../../Context/LoaderContext";
import { openNotification } from "../../../../Components/Notification";
import organizationService from "../../../../Service/organization.service";
import "./index.scss";

interface SSOConfigurationModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  org: Organization;
  isSSOEnabled: boolean;
  setOrg: React.Dispatch<React.SetStateAction<Organization | null>>;
}

const SSOConfigurationModal: React.FC<SSOConfigurationModalProps> = ({
  open,
  setOpen,
  org,
  isSSOEnabled,
  setOrg,
}) => {
  const [form] = Form.useForm();
  const { setLoading, setLoadingMessage, isLoading } = useLoader();

  const validateUrl = (_: any, value: string) => {
    if (!value) {
      return Promise.resolve();
    }

    try {
      new URL(value);
      return Promise.resolve();
    } catch {
      return Promise.reject(new Error("Please enter a valid URL"));
    }
  };

  const handleClose = () => {
    form.resetFields();
    setOpen(false);
  };

  const handleConfigureSSOConfiguration = async (values: {
    oauthProviderUrl: string;
    oauthTokenUrl: string;
    oauthClientId: string;
    oauthClientSecret: string;
    oauthScopes: string[];
    oauthUserInfoUrl: string;
    oauthVersion: string;
  }) => {
    try {
      setLoading(true);
      setLoadingMessage(
        isSSOEnabled ? "Updating SSO configuration..." : "Configuring SSO..."
      );

      const updatedOrg = await organizationService.updateOrganization({
        _id: org._id,
        oauthProviderUrl: values.oauthProviderUrl,
        oauthTokenUrl: values.oauthTokenUrl,
        oauthClientId: values.oauthClientId,
        oauthClientSecret: values.oauthClientSecret,
        oauthScopes: values.oauthScopes,
        oauthUserInfoUrl: values.oauthUserInfoUrl,
        oauthVersion: values.oauthVersion,
      });

      if (updatedOrg) {
        setOrg(updatedOrg);
        openNotification(
          "success",
          "Success",
          isSSOEnabled
            ? "SSO configuration updated successfully"
            : "SSO configured successfully"
        );
        handleClose();
      }
    } catch (error: any) {
      console.error(
        isSSOEnabled
          ? "Error updating SSO configuration:"
          : "Error configuring SSO:",
        error
      );
      openNotification(
        "error",
        "Error",
        error.message ||
          (isSSOEnabled
            ? "Failed to update SSO configuration"
            : "Failed to configure SSO")
      );
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleSubmit = (values: {
    oauthProviderUrl: string;
    oauthTokenUrl: string;
    oauthClientId: string;
    oauthClientSecret: string;
    oauthScopes: string[];
    oauthUserInfoUrl: string;
    oauthVersion: string;
  }) => {
    handleConfigureSSOConfiguration(values);
  };

  useEffect(() => {
    if (open) {
      if (isSSOEnabled && org) {
        form.setFieldsValue({
          oauthProviderUrl: org.oauthProviderUrl || "",
          oauthTokenUrl: org.oauthTokenUrl || "",
          oauthClientId: org.oauthClientId || "",
          oauthClientSecret: org.oauthClientSecret || "",
          oauthScopes: org.oauthScopes || [],
          oauthUserInfoUrl: org.oauthUserInfoUrl || "",
          oauthVersion: org.oauthVersion || "2.0",
        });
      } else {
        form.resetFields();
      }
    }
  }, [isSSOEnabled, form, org, open]);

  return (
    <Modal
      title={isSSOEnabled ? "Update SSO Configuration" : "SSO Configuration"}
      open={open}
      onCancel={handleClose}
      footer={null}
      maskClosable={isLoading ? false : true}
      destroyOnClose
      centered
      width={600}
    >
      <section className="sso-configuration-modal-content">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="sso-configuration-modal-form"
        >
          <Form.Item
            label="OAuth Provider URL"
            name="oauthProviderUrl"
            rules={[
              { required: true, message: "Please enter OAuth Provider URL" },
              { validator: validateUrl },
            ]}
            style={{ width: "100%", margin: 0 }}
          >
            <Input placeholder="https://example.com/oauth/authorize" />
          </Form.Item>

          <Form.Item
            label="OAuth Token URL"
            name="oauthTokenUrl"
            rules={[
              { required: true, message: "Please enter OAuth Token URL" },
              { validator: validateUrl },
            ]}
            style={{ width: "100%", margin: 0 }}
          >
            <Input placeholder="https://example.com/oauth/token" />
          </Form.Item>

          <Form.Item
            label="OAuth Client ID"
            name="oauthClientId"
            rules={[
              { required: true, message: "Please enter OAuth Client ID" },
            ]}
            style={{ width: "100%", margin: 0 }}
          >
            <Input placeholder="Enter your OAuth Client ID" />
          </Form.Item>

          <Form.Item
            label="OAuth Client Secret"
            name="oauthClientSecret"
            rules={[
              { required: true, message: "Please enter OAuth Client Secret" },
            ]}
            style={{ width: "100%", margin: 0 }}
          >
            <Input.Password placeholder="Enter your OAuth Client Secret" />
          </Form.Item>

          <Form.Item
            label="OAuth Scopes"
            name="oauthScopes"
            rules={[
              {
                required: true,
                message: "Please enter at least one OAuth scope",
              },
            ]}
            tooltip="Enter OAuth scopes separated by commas (e.g., read, write, admin)"
            style={{ width: "100%", margin: 0 }}
          >
            <Select
              mode="tags"
              placeholder="Enter OAuth scopes (e.g., read, write, admin)"
              tokenSeparators={[","]}
              style={{ width: "100%" }}
              open={false}
              onDropdownVisibleChange={() => {}}
              dropdownStyle={{ display: "none" }}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label="OAuth User Info URL"
            name="oauthUserInfoUrl"
            rules={[
              { required: true, message: "Please enter OAuth User Info URL" },
              { validator: validateUrl },
            ]}
            style={{ width: "100%", margin: 0 }}
          >
            <Input placeholder="https://example.com/oauth/userinfo" />
          </Form.Item>

          <Form.Item
            label="OAuth Version"
            name="oauthVersion"
            rules={[
              { required: true, message: "Please select OAuth Version" },
            ]}
            style={{ width: "100%", margin: 0 }}
          >
            <Select placeholder="Select OAuth Version">
              <Select.Option value="2.0">2.0</Select.Option>
              <Select.Option value="2.1">2.1</Select.Option>
            </Select>
          </Form.Item>
        </Form>

        <Divider style={{ margin: "0" }} />

        <div className="sso-configuration-modal-footer">
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={isLoading}
            onClick={() => form.submit()}
          >
            {isSSOEnabled ? "Update" : "Configure"}
          </Button>
        </div>
      </section>
    </Modal>
  );
};

export default SSOConfigurationModal;
